import { useQuery } from '@tanstack/react-query';
import type { InquiryFieldDefinition, InquiryType } from '@kontor/shared';
import { api } from './api';

/** Anfragetypen des Mandanten. Ändern sich selten, deshalb lange zwischengespeichert. */
export function useInquiryTypes() {
  const query = useQuery({
    queryKey: ['inquiry-types'],
    queryFn: async () => (await api<{ data: InquiryType[] }>('GET', '/inquiry-types')).data,
    staleTime: 5 * 60_000,
  });
  const byKey = new Map((query.data ?? []).map((t) => [t.key, t]));
  return {
    isLoading: query.isLoading,
    types: query.data ?? [],
    get: (key: string) => byKey.get(key),
    /** Bezeichnung des Typs, ohne Definition der Schlüssel selbst */
    label: (key: string) => byKey.get(key)?.label ?? key,
  };
}

/** Felder ohne Definition: "from_address" → "From address" ist besser als nichts */
function fallbackLabel(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const numberFormat = new Intl.NumberFormat('de-DE');
const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long' });

function formatValue(def: InquiryFieldDefinition | undefined, value: unknown): string {
  if (value === null || value === undefined || value === '') return '–';
  if (!def) return typeof value === 'object' ? JSON.stringify(value) : String(value);
  const option = (v: unknown) => def.options?.find((o) => o.value === String(v))?.label ?? String(v);
  switch (def.type) {
    case 'number':
      return typeof value === 'number' ? `${numberFormat.format(value)}${def.unit ? ` ${def.unit}` : ''}` : String(value);
    case 'boolean':
      return value === true ? 'Ja' : value === false ? 'Nein' : String(value);
    case 'date':
      return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? dateFormat.format(new Date(value)) : String(value);
    case 'select':
      return option(value);
    case 'multiselect':
      return Array.isArray(value) ? value.map(option).join(', ') : option(value);
    default:
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
  }
}

/**
 * Branchenfelder zur Anzeige: zuerst die definierten Felder in ihrer Reihenfolge,
 * danach Felder ohne Definition (z. B. wenn die Webseite ein neues Feld mitschickt).
 */
export function displayFields(type: InquiryType | undefined, fields: Record<string, unknown>): { key: string; label: string; value: string }[] {
  const defs = type?.fields ?? [];
  const known = defs.filter((d) => d.key in fields).map((d) => ({ key: d.key, label: d.label, value: formatValue(d, fields[d.key]) }));
  const definedKeys = new Set(defs.map((d) => d.key));
  const rest = Object.entries(fields)
    .filter(([k]) => !definedKeys.has(k))
    .map(([k, v]) => ({ key: k, label: fallbackLabel(k), value: formatValue(undefined, v) }));
  return [...known, ...rest];
}
